import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';
import { getChecklistById, getAllCheckLists } from '../helpers/checklist-api';
import socket from '../helpers/socket-connect';
import { sortByAlphapeticalOrder } from '../helpers/helpers';
import errorHandling from '../helpers/error-handling';
import useAuth from '../hooks/useAuth';

/*
  Types
*/
import { TaskObj } from '../helpers/types';

/*
  Components
*/
import SnackBar from '../components/SnackBar';
import Loader from '../components/Loader';
import Column from '../components/Column';
import BreadCrumbs from '../components/BreadCrumbs';

const TaskList = () => {
  const { token } = useAuth();
  const [checklists, setChecklists] = useState<any[]>([]);
  const { checklistId } = useParams<{checklistId: string}>();
  const [checklistData, setChecklistData] = useState<any>();
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    socket.emit('room', checklistId);
    socket.on('newChecklistData', (newChecklistData: any[]) => {
      setChecklistData(newChecklistData);
    });
  }, []);

  useEffect(() => {
    getAllCheckLists(token)
      .then(errorHandling)
      .then((data: any) => {
        setChecklists(sortByAlphapeticalOrder(data));
      })
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    getChecklistById(checklistId, token)
      .then(errorHandling)
      .then((data: any) => {
        setChecklistData(data);
      })
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    localStorage.checklists = JSON.stringify(checklists);
  }, [checklists]);

  const handleAddTask = (taskTitle: string) => {
    const newTaskObj: TaskObj = {
      id: uuidv4(),
      taskTitle,
      taskDescription: '',
      taskPrice: '',
      isDone: false,
    };
    const newTasks = [...checklistData.tasks];
    newTasks.push(newTaskObj);
    const newTaskStateObj = {
      ...checklistData.states.pending,
    };
    const newTaskIds = [...checklistData.states.pending.taskIds];
    newTaskIds.push(newTaskObj.id);
    const newChecklistData = {
      ...checklistData,
      tasks: newTasks,
      states: {
        ...checklistData.states,
        pending: {
          ...newTaskStateObj,
          taskIds: newTaskIds,
        },
      },
    };
    // console.log(newChecklistData);
    setChecklistData(newChecklistData);
    socket.emit('newChecklistData', checklistId, newChecklistData);
  };

  const handleUpdateTask = (updatedObj: TaskObj) => {
    const newTasks = [...checklistData.tasks];
    const filteredTasks = newTasks.filter(task => task.id !== updatedObj.id);
    filteredTasks.push(updatedObj);
    const newChecklistData = {
      ...checklistData,
      tasks: filteredTasks,
    };
    setChecklistData(newChecklistData);
    socket.emit('newChecklistData', checklistId, newChecklistData);
  };

  const handleDragEnd = (result: any) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }
    const start = checklistData.states[source.droppableId];
    const finish = checklistData.states[destination.droppableId];

    if (start === finish) {
      const newTaskIds = [...start.taskIds];
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);
      const newColumn = {
        ...start,
        taskIds: newTaskIds,
      };
      const newChecklistData = {
        ...checklistData,
        states: {
          ...checklistData.states,
          [newColumn.id]: newColumn,
        },
      };
      setChecklistData(newChecklistData);
      socket.emit('newChecklistData', checklistId, newChecklistData);
    } else {
      const startTaskIds = [...start.taskIds];
      startTaskIds.splice(source.index, 1);
      const newStart = {
        ...start,
        taskIds: startTaskIds,
      };
      const finishTaskIds = [...finish.taskIds];
      finishTaskIds.splice(destination.index, 0, draggableId);
      const newFinish = {
        ...finish,
        taskIds: finishTaskIds,
      };
      const newTasks = [...checklistData.tasks];
      const taskIndex = newTasks.findIndex((task: TaskObj) => task.id === draggableId);
      const TaskBeingMoved = newTasks[taskIndex];
      TaskBeingMoved.isDone = !TaskBeingMoved.isDone;
      newTasks.splice(taskIndex, 1, TaskBeingMoved);
      const newChecklistData = {
        ...checklistData,
        states: {
          ...checklistData.states,
          [newStart.id]: newStart,
          [newFinish.id]: newFinish,
        },
      };
      setChecklistData(newChecklistData);
      socket.emit('newChecklistData', checklistId, newChecklistData);
    }
  };

  const getColumnTasks = (arrayOfTaskIds: any[]) => {
    const filteredTasks = arrayOfTaskIds.map((taskId: string) => {
      const dataTasks = checklistData.tasks;
      const newDataTasks = dataTasks.filter((task: any) => task.id === taskId)[0];
      return newDataTasks;
    });
    return filteredTasks;
  };

  return (
    <>
      {error && <SnackBar state={error} setState={setError} type="error" message="There was an error" />}
      {isLoading && <Loader />}
      {!isLoading && checklistData && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <section className="content__container tasklist">
            <BreadCrumbs />
            <div className="tasks">
              <Column
                handleAddTask={handleAddTask}
                handleUpdateTask={handleUpdateTask}
                key={checklistData.states.pending.id}
                column={checklistData.states.pending}
                tasks={getColumnTasks(checklistData.states.pending.taskIds)} />
              <Column
                handleUpdateTask={handleUpdateTask}
                key={checklistData.states.done.id}
                column={checklistData.states.done}
                tasks={getColumnTasks(checklistData.states.done.taskIds)} />
            </div>
          </section>
        </DragDropContext>
      )}
    </>
  );
};

export default TaskList;
